import React, { useState, useEffect, useRef, useCallback } from 'react';
import { eventsAPI } from '../../services/api';
import ImageCropper from './ImageCropper';
import './EventsAdmin.css';

const BACKEND_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
const EMPTY_FORM = { title: '', description: '', isActive: true };

// Resolve an event's image to a displayable src.
// Accepts an optional localUrl (blob URL from cropper) to override stored path.
const resolveImg = (event, backendUrl, localUrl = null) => {
  if (localUrl) return localUrl;
  if (event.image_path) return `${backendUrl}${event.image_path}`;
  return null;
};

// ── Preview: square popup card — mirrors real modal layout ───────────────────
const PopupPreview = ({ event, backendUrl, localUrl, onEdit }) => {
  const imgSrc = resolveImg(event, backendUrl, localUrl);
  return (
    <div className={`preview-popup${event.is_active ? '' : ' preview-inactive'}`}>
      {/* Top: title left, image top-right */}
      <div className="preview-popup-top">
        <span className="preview-popup-title">{event.title || <em>Event Title</em>}</span>
        {imgSrc && (
          <div className="preview-popup-img">
            <img src={imgSrc} alt={event.title} />
          </div>
        )}
      </div>

      {event.description && <div className="preview-popup-divider" />}

      {/* Description fills remaining space */}
      {event.description && (
        <div className="preview-popup-desc-wrap">
          <p className="preview-popup-desc">{event.description}</p>
        </div>
      )}

      {/* Footer: hidden badge + edit button */}
      <div className="preview-popup-footer">
        {!event.is_active && <span className="inactive-badge">Hidden</span>}
        {onEdit && (
          <button className="btn-edit-overlay" onClick={onEdit}>Edit</button>
        )}
      </div>
    </div>
  );
};

// ── Preview: carousel item appearance ────────────────────────────────────────
const CarouselPreview = ({ event, backendUrl, localUrl }) => {
  const imgSrc = resolveImg(event, backendUrl, localUrl);
  return (
    <div className="preview-carousel-item">
      <div className="preview-carousel-img">
        {imgSrc
          ? <img src={imgSrc} alt={event.title} />
          : <span className="preview-carousel-placeholder">📅</span>
        }
      </div>
      <p className="preview-carousel-title">{event.title || 'Event Title'}</p>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const EventsAdmin = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [mode, setMode] = useState('list'); // 'list' | 'add' | 'edit'
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [pendingFile, setPendingFile] = useState(null);
  const [croppedBlob, setCroppedBlob] = useState(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const fileInputRef = useRef(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await eventsAPI.getAll();
      setEvents(res.data.events || []);
    } catch {
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Revoke old blob URL when it changes
  useEffect(() => {
    return () => { if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl); };
  }, [localPreviewUrl]);

  const openAdd = () => {
    setEditingEvent(null);
    setForm(EMPTY_FORM);
    setCroppedBlob(null);
    setLocalPreviewUrl(null);
    setPendingFile(null);
    setError('');
    setSuccess('');
    setMode('add');
  };

  const openEdit = (ev) => {
    setEditingEvent(ev);
    setForm({ title: ev.title, description: ev.description || '', isActive: ev.is_active });
    setCroppedBlob(null);
    setLocalPreviewUrl(null);
    setPendingFile(null);
    setError('');
    setSuccess('');
    setMode('edit');
  };

  const cancelForm = () => {
    setCroppedBlob(null);
    if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    setLocalPreviewUrl(null);
    setPendingFile(null);
    setMode('list');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPendingFile(file);
    setShowCropper(true);
    e.target.value = '';
  };

  const handleCropConfirm = (blob) => {
    if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    const url = URL.createObjectURL(blob);
    setCroppedBlob(blob);
    setLocalPreviewUrl(url);
    setPendingFile(null);
    setShowCropper(false);
  };

  const handleCropCancel = () => {
    setPendingFile(null);
    setShowCropper(false);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const fd = new FormData();
      fd.append('title', form.title.trim());
      fd.append('description', form.description);
      fd.append('is_active', String(form.isActive));
      if (croppedBlob) fd.append('image', croppedBlob, 'event.jpg');

      if (mode === 'add') {
        await eventsAPI.create(fd);
        setSuccess('Event created');
      } else {
        await eventsAPI.update(editingEvent.id, fd);
        setSuccess('Event updated');
      }
      await fetchEvents();
      setCroppedBlob(null);
      setLocalPreviewUrl(null);
      setMode('list');
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to save event');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event? This cannot be undone.')) return;
    setLoading(true);
    setError('');
    try {
      await eventsAPI.delete(id);
      await fetchEvents();
      setSuccess('Event deleted');
      setCroppedBlob(null);
      setLocalPreviewUrl(null);
      setMode('list');
    } catch {
      setError('Failed to delete event');
    } finally {
      setLoading(false);
    }
  };

  // ── Cropper overlay ───────────────────────────────────────────────────────
  if (showCropper && pendingFile) {
    return (
      <ImageCropper
        file={pendingFile}
        onConfirm={handleCropConfirm}
        onCancel={handleCropCancel}
      />
    );
  }

  // ── Add / Edit form ───────────────────────────────────────────────────────
  if (mode === 'add' || mode === 'edit') {
    // For preview: use local blob if available, otherwise fall back to stored path
    const previewEvent = {
      ...editingEvent,
      title: form.title,
      description: form.description,
      is_active: form.isActive,
      image_path: croppedBlob ? null : (editingEvent?.image_path ?? null)
    };

    return (
      <div className="events-admin-form-page">
        <div className="events-admin-form-card">
          <div className="events-admin-form-header">
            <h2>{mode === 'add' ? 'Add Event' : 'Edit Event'}</h2>
            <button className="btn btn-secondary" onClick={cancelForm}>Cancel</button>
          </div>

          {error && <div className="events-admin-error">{error}</div>}

          <div className="events-admin-form-body">
            {/* ── Left: fields ── */}
            <div className="events-admin-fields">
              <div className="form-group">
                <label htmlFor="evt-title">Title *</label>
                <input
                  id="evt-title"
                  name="title"
                  value={form.title}
                  onChange={handleFormChange}
                  placeholder="Event title"
                  className="input-field"
                />
              </div>

              <div className="form-group">
                <label htmlFor="evt-desc">Description</label>
                <textarea
                  id="evt-desc"
                  name="description"
                  value={form.description}
                  onChange={handleFormChange}
                  placeholder="Describe this event…"
                  className="input-field textarea-field"
                  rows={5}
                />
              </div>

              <div className="form-group form-group-check">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={form.isActive}
                    onChange={handleFormChange}
                  />
                  <span>Show on website</span>
                </label>
              </div>

              <div className="form-group">
                <label>Image</label>
                <div className="image-upload-area">
                  {(localPreviewUrl || (editingEvent?.image_path && !croppedBlob)) && (
                    <div className="image-upload-preview">
                      <img
                        src={localPreviewUrl || `${BACKEND_URL}${editingEvent.image_path}`}
                        alt="preview"
                      />
                    </div>
                  )}
                  <div className="image-upload-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {localPreviewUrl || editingEvent?.image_path ? 'Replace Image' : 'Select Image'}
                    </button>
                    <span className="image-hint">Scroll to zoom · Drag to reposition · Square crop</span>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                </div>
              </div>

              <div className="events-admin-form-actions">
                {mode === 'edit' && (
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDelete(editingEvent.id)}
                    disabled={loading}
                  >
                    Delete Event
                  </button>
                )}
                <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
                  {loading ? 'Saving…' : 'Save Event'}
                </button>
              </div>
            </div>

            {/* ── Right: live previews ── */}
            <div className="events-admin-previews">
              <p className="preview-section-label">Popup preview</p>
              <PopupPreview
                event={previewEvent}
                backendUrl={BACKEND_URL}
                localUrl={localPreviewUrl}
              />

              <p className="preview-section-label" style={{ marginTop: 24 }}>Carousel item</p>
              <CarouselPreview
                event={previewEvent}
                backendUrl={BACKEND_URL}
                localUrl={localPreviewUrl}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── List view ──────────────────────────────────────────────────────────────
  return (
    <div className="events-admin-list-page">
      <div className="events-admin-list-header">
        <h2>Events</h2>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Event</button>
      </div>

      {error && <div className="events-admin-error">{error}</div>}
      {success && <div className="events-admin-success">{success}</div>}
      {loading && <div className="events-admin-loading">Loading…</div>}

      {!loading && events.length === 0 && (
        <div className="events-admin-empty">
          <p>No events yet. Add one to show a carousel on the homepage.</p>
        </div>
      )}

      {!loading && events.length > 0 && (
        <div className="events-admin-event-list">
          {events.map(ev => (
            <div key={ev.id} className="events-admin-event-row">
              <div className="event-row-popup">
                <p className="event-row-label">Popup view</p>
                <PopupPreview
                  event={ev}
                  backendUrl={BACKEND_URL}
                  localUrl={null}
                  onEdit={() => openEdit(ev)}
                />
              </div>
              <div className="event-row-carousel">
                <p className="event-row-label">Carousel item</p>
                <CarouselPreview event={ev} backendUrl={BACKEND_URL} localUrl={null} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventsAdmin;
